import { createContext, useContext } from 'react';
import type { BookShellContext } from './types';

/**
 * Book-shell React context (2026-08-07 fix — findings 7 / 10 / 12).
 *
 * ═══ WHY THIS EXISTS ══════════════════════════════════════════════════
 *
 * BookShell used to hand its context to child routes via React
 * Router's `<Outlet context={context}>`. That surface breaks in the
 * production route tree because React Router v7 does NOT propagate
 * outlet context through nested Outlets. The book flow's real chain
 * is:
 *
 *   <Route element={<BookShell />}>          // renders <Outlet context={ctx}>
 *     <Route element={<ProtectedRoute />}>   // renders bare <Outlet /> at ProtectedRoute.tsx:13
 *       <Route path="/book/:orderId" element={<BookRoute />} />
 *     </Route>
 *   </Route>
 *
 * ProtectedRoute's bare `<Outlet />` drops the context. BookRoute
 * called `useOutletContext<BookShellContext>()`, got `undefined`,
 * destructured `envelope`, and threw a TypeError caught by the
 * top-level Sentry ErrorBoundary — showing "Oops! Da ist etwas
 * schiefgelaufen." for every book status the customer visited.
 *
 * Forwarding the context through ProtectedRoute would repair one
 * guard and break at the next. React context is the honest
 * mechanism: it propagates through any component tree regardless of
 * router nesting.
 *
 * ═══ NAMED ERROR ═══════════════════════════════════════════════════
 *
 * `useBookShellContext` throws `BookShellContextMissingError` when
 * consumed outside the Provider. The point is that the next failure
 * of this class says WHAT is wrong, not the generic "Cannot
 * destructure property 'envelope' of undefined" that hid the
 * original defect for hours inside the ErrorBoundary.
 *
 * ═══ FILE SPLIT ═══════════════════════════════════════════════════
 *
 * The Provider component lives in
 * `./book-shell-context-provider.tsx`. This split is deliberate: the
 * `react-refresh/only-export-components` lint rule fires when a file
 * mixes component and non-component exports (createContext,
 * hook, error class are non-components; the Provider is). The two
 * files share the internal context via the `BookShellReactContext`
 * export below — consumers should still use `useBookShellContext`,
 * which enforces the fail-loud contract.
 *
 * ═══ WHERE THE INTERFACE LIVES ════════════════════════════════════
 *
 * `BookShellContext` (the value shape) is declared in
 * `./types.ts` next to the other book types. Both this file and
 * `BookShell.tsx` import from `./types` — no import cycle between
 * the provider and the shell.
 */

/**
 * Raw React context object. Exported ONLY for the Provider file to
 * consume via `BookShellReactContext.Provider`. Consumers should
 * NOT read this directly — use `useBookShellContext` below so the
 * missing-Provider case throws the named error class.
 */
export const BookShellReactContext = createContext<BookShellContext | undefined>(undefined);

/**
 * Named error thrown by `useBookShellContext` when consumed outside
 * the Provider. The name + message are deliberately actionable — a
 * "Cannot destructure" TypeError vanishing into the Sentry
 * ErrorBoundary is exactly the failure mode this fix removes.
 */
export class BookShellContextMissingError extends Error {
  constructor() {
    super(
      'useBookShellContext must be used inside a <BookShellContextProvider>. ' +
        'This usually means a book route was mounted outside BookShell — ' +
        'check the route tree in pwa/src/App.tsx.',
    );
    this.name = 'BookShellContextMissingError';
  }
}

/**
 * Consume the shell context. Throws BookShellContextMissingError
 * when the Provider is absent. The throw is deliberate: the next
 * refactor that breaks the wire fails LOUD with a named class the
 * ErrorBoundary can surface, not silently through a destructure.
 */
export function useBookShellContext(): BookShellContext {
  const value = useContext(BookShellReactContext);
  if (value === undefined) {
    throw new BookShellContextMissingError();
  }
  return value;
}
